import React, { FC, useCallback, useMemo } from 'react'
import { Checkbox, IconButton, Tooltip } from '@mui/material'
import { makeStyles } from '@mui/styles'
import { Theme } from '@mui/material/styles'
import { useDispatch, useSelector } from 'react-redux'
import { getListOfClientTypesData } from '../../store/selectors'
import { updateAssetRequest } from '../../store/actions'

const useStyles = makeStyles((theme: Theme) => ({
  checkbox: {
    padding: 0,
    color: theme.palette.secondary.main,
    '&.Mui-checked': {
      color: theme.palette.secondary.main,
    },
    '&:hover': {
      color: theme.palette.secondary.light,
    },
  },
}))

export interface ClientType {
  id: string
  name: string
}

interface TableClientTypesRowProps {
  assetId: number
  fireblockBaseAssetsId: string
  fireblockQuoteAssetsId: string
  clientTypes: ClientType[]
}

const DISPLAY_ORDER = ['Payment client', 'Investments client', 'Trading client']

export const TableClientTypesRow: FC<TableClientTypesRowProps> = ({
  assetId,
  fireblockBaseAssetsId,
  fireblockQuoteAssetsId,
  clientTypes,
}) => {
  const classes = useStyles()
  const dispatch = useDispatch()

  const listOfClientTypes = useSelector(getListOfClientTypesData)

  const selectedIds = useMemo(() => clientTypes.map((ct) => ct.id), [clientTypes])

  // to statically order the client types
  const orderedClientTypes = useMemo(
    () =>
      DISPLAY_ORDER.map((label) => listOfClientTypes.find((ct) => ct.name === label)).filter((ct): ct is ClientType =>
        Boolean(ct),
      ),
    [listOfClientTypes],
  )

  const handleCheck = useCallback(
    (id: string, isChecked: boolean) => {
      dispatch(
        updateAssetRequest({
          ids: [assetId],
          fireblockBaseAssetsId,
          fireblockQuoteAssetsId,
          clientType: isChecked ? selectedIds.filter((selectedId) => selectedId !== id) : [...selectedIds, id],
        }),
      )
    },
    [dispatch, assetId, selectedIds, fireblockBaseAssetsId, fireblockQuoteAssetsId],
  )

  return (
    <>
      {orderedClientTypes.map((clientType) => (
        <Tooltip title={clientType.name} key={clientType.id}>
          <IconButton
            sx={{ padding: 0 }}
            onClick={() => handleCheck(clientType.id, selectedIds.includes(clientType.id))}
          >
            <Checkbox size="small" className={classes.checkbox} checked={selectedIds.includes(clientType.id)} />
          </IconButton>
        </Tooltip>
      ))}
    </>
  )
}
